import type { CalendarEvent, LensId } from '@/lib/types';
import type { LongWeekend } from '@/lib/long-weekends';
import { formatDateWithDay, formatDateWithDayLong, formatDayLong } from '@/lib/date-format';
import { durationDaysInclusive } from '@/lib/date-calc';
import { getEventColor } from '@/lib/constants';
import { uiFb } from '@/lib/i18n';
import { isPublicHoliday } from '@/lib/event-filters';

interface SmartHeroProps {
  events: CalendarEvent[];
  year: number;
  lens: LensId;
  lang: string;
  longWeekends?: LongWeekend[] | null;
}

interface HeroCard {
  title: string;
  subtitle: string;
  badge: string;
  badgeColor: string;
  eventColor: string;
}

/** Build hero cards for the country lens — focus on long weekends and key holidays */
function buildCountryCards(events: CalendarEvent[], longWeekends: LongWeekend[], lang: string): HeroCard[] {
  const cards: HeroCard[] = [];
  const publicHolidays = events.filter(isPublicHoliday);

  // Best long weekends (sorted by total days off, descending)
  const sorted = [...longWeekends].sort((a, b) => b.totalDaysOff - a.totalDaysOff);
  for (const lw of sorted.slice(0, 3)) {
    const dow = formatDayLong(lw.holiday.dateStart, lang);
    const badge = lw.leaveDaysNeeded === 0
      ? uiFb('ui.hero_natural_lw', '{days}-day weekend', lang, { days: String(lw.totalDaysOff) })
      : uiFb('ui.hero_bridge_lw', 'Take {take}, get {get} off', lang, { take: String(lw.leaveDaysNeeded), get: String(lw.totalDaysOff) });

    cards.push({
      title: lw.holiday.title,
      subtitle: `${dow}, ${formatDateWithDay(lw.holiday.dateStart, lang)}`,
      badge,
      badgeColor: lw.leaveDaysNeeded === 0 ? 'bg-semantic-success/10 text-semantic-success' : 'bg-semantic-warning/10 text-semantic-warning',
      eventColor: getEventColor(lw.holiday.type),
    });
  }

  // If fewer than 3 long weekends, fill with significant holidays not already shown
  const shownIds = new Set(cards.map(c => c.title));
  if (cards.length < 4) {
    const extras = publicHolidays
      .filter(e => !shownIds.has(e.title))
      .sort((a, b) => {
        const sigRank: Record<string, number> = { critical: 0, highest: 1, high: 2, major: 3, notable: 4, medium: 5, low: 6 };
        return (sigRank[a.significance] ?? 6) - (sigRank[b.significance] ?? 6);
      })
      .slice(0, 5 - cards.length);

    for (const e of extras) {
      const dow = formatDayLong(e.dateStart, lang);
      cards.push({
        title: e.title,
        subtitle: `${dow}, ${formatDateWithDay(e.dateStart, lang)}`,
        badge: e.type === 'national_day'
          ? uiFb('ui.hero_national_day', 'National Day', lang)
          : uiFb('ui.hero_public_holiday', 'Public Holiday', lang),
        badgeColor: 'bg-brand/10 text-brand',
        eventColor: getEventColor(e.type),
      });
    }
  }

  return cards.slice(0, 5);
}

/** Build hero cards for the religion lens — duration + Hijri dates */
function buildReligionCards(events: CalendarEvent[], lang: string): HeroCard[] {
  const cards: HeroCard[] = [];

  // Sort by significance, then date
  const sorted = [...events].sort((a, b) => {
    const sigRank: Record<string, number> = { critical: 0, highest: 1, high: 2, major: 3, notable: 4, medium: 5, low: 6 };
    const sigDiff = (sigRank[a.significance] ?? 6) - (sigRank[b.significance] ?? 6);
    if (sigDiff !== 0) return sigDiff;
    return a.dateStart.localeCompare(b.dateStart);
  });

  for (const e of sorted.slice(0, 5)) {
    const days = durationDaysInclusive(e.dateStart, e.dateEnd);
    const subtitle = days > 1
      ? `${formatDateWithDay(e.dateStart, lang)} – ${formatDateWithDay(e.dateEnd, lang)}`
      : formatDateWithDay(e.dateStart, lang);
    const badge = days > 1
      ? uiFb('ui.hero_duration', '{days} days', lang, { days: String(days) })
      : formatDayLong(e.dateStart, lang);

    cards.push({
      title: e.titleLocal || e.title,
      subtitle,
      badge,
      badgeColor: 'bg-brand/10 text-brand',
      eventColor: getEventColor(e.type),
    });
  }

  return cards;
}

/** Build hero cards for the academic lens — key milestones */
function buildAcademicCards(events: CalendarEvent[], lang: string): HeroCard[] {
  const cards: HeroCard[] = [];

  const sorted = [...events].sort((a, b) => {
    const sigRank: Record<string, number> = { critical: 0, highest: 1, high: 2, major: 3, notable: 4, medium: 5, low: 6 };
    const sigDiff = (sigRank[a.significance] ?? 6) - (sigRank[b.significance] ?? 6);
    if (sigDiff !== 0) return sigDiff;
    return a.dateStart.localeCompare(b.dateStart);
  });

  for (const e of sorted.slice(0, 5)) {
    cards.push({
      title: e.titleLocal || e.title,
      subtitle: formatDateWithDayLong(e.dateStart, lang),
      badge: e.type === 'enrollment_deadline'
        ? uiFb('ui.hero_deadline', 'Deadline', lang)
        : e.type === 'exam_period'
          ? uiFb('ui.hero_exam', 'Exam', lang)
          : uiFb('ui.hero_milestone', 'Milestone', lang),
      badgeColor: e.type === 'enrollment_deadline' ? 'bg-semantic-error/10 text-semantic-error' : 'bg-brand/10 text-brand',
      eventColor: getEventColor(e.type),
    });
  }

  return cards;
}

/** Build hero cards for the nature lens — peak window duration */
function buildNatureCards(events: CalendarEvent[], lang: string): HeroCard[] {
  const cards: HeroCard[] = [];

  const sorted = [...events].sort((a, b) => a.dateStart.localeCompare(b.dateStart));

  for (const e of sorted.slice(0, 5)) {
    const days = durationDaysInclusive(e.dateStart, e.dateEnd);
    const subtitle = days > 1
      ? `${formatDateWithDay(e.dateStart, lang)} – ${formatDateWithDay(e.dateEnd, lang)}`
      : formatDateWithDay(e.dateStart, lang);

    cards.push({
      title: e.title,
      subtitle,
      badge: days > 1
        ? uiFb('ui.hero_window', '{days}-day window', lang, { days: String(days) })
        : formatDayLong(e.dateStart, lang),
      badgeColor: 'bg-semantic-success/10 text-semantic-success',
      eventColor: getEventColor(e.type),
    });
  }

  return cards;
}

function buildCards(events: CalendarEvent[], lens: LensId, lang: string, longWeekends: LongWeekend[]): HeroCard[] {
  switch (lens) {
    case 'religion': return buildReligionCards(events, lang);
    case 'academic': return buildAcademicCards(events, lang);
    case 'nature': return buildNatureCards(events, lang);
    default: return buildCountryCards(events, longWeekends, lang);
  }
}

export function SmartHero({ events, year, lens, lang, longWeekends }: SmartHeroProps) {
  const cards = buildCards(events, lens, lang, longWeekends ?? []);
  if (cards.length === 0) return null;

  return (
    <div className="mx-auto mb-6 w-full max-w-6xl px-4 md:px-8 lg:px-16 xl:px-20">
      <div
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin sm:grid sm:grid-cols-3 sm:overflow-visible md:grid-cols-5 md:gap-4"
        role="list"
        aria-label={uiFb('ui.hero_highlights', 'Key highlights', lang)}
      >
        {cards.map((card, i) => (
          <div
            key={i}
            role="listitem"
            className="flex min-w-[200px] flex-shrink-0 flex-col gap-1.5 rounded-xl border border-border-subtle bg-surface-card p-3 sm:min-w-0 md:p-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: card.eventColor }}
              />
              <span className="truncate text-sm font-semibold text-text-primary">
                {card.title}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-text-secondary">
              {card.subtitle}
            </p>
            <span className={`mt-auto inline-block w-fit rounded-full px-2 py-0.5 text-caption font-semibold sm:text-label ${card.badgeColor}`}>
              {card.badge}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
